import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useUserAuth } from '@/providers/UserAuthProvider';
import { useAccount, useSignMessage } from 'wagmi';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { XLogo } from '@/components/icons/XLogo';

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AuthModal({ open, onOpenChange }: AuthModalProps) {
  const { signInWithGoogle, signInWithTwitter, signInWithWallet, signUpWithEmail, signInWithEmail, resendVerificationEmail, resetPassword } = useUserAuth();
  const { isConnected, address } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { open: openWeb3Modal } = useWeb3Modal();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    upper: false,
    lower: false,
    number: false,
    special: false,
  });
  const [showReset, setShowReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [referralCode, setReferralCode] = useState('');

  const computeChecks = (pwd: string) => ({
    length: pwd.length >= 6,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    number: /\d/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd),
  });

  const updatePassword = (pwd: string) => {
    setPassword(pwd);
    setPasswordChecks(computeChecks(pwd));
  };

  const handleGoogle = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithGoogle(referralCode || undefined);
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.message || 'Google sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTwitter = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithTwitter(referralCode || undefined);
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.message || 'X sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleWallet = async () => {
    try {
      setLoading(true);
      setError('');
      if (!isConnected || !address) {
        // Open wallet connect modal automatically using Web3Modal
        try {
          await openWeb3Modal({ view: 'Connect' });
          // Don't show error - wallet modal opened successfully
        } catch (modalError) {
          console.error('Failed to open wallet modal:', modalError);
          setError('Failed to open wallet connection. Please try the Connect Wallet button in the header.');
        }
        setLoading(false);
        return;
      }
      const nonce = Date.now();
      const message = `MdbApp Wallet Login\nAddress: ${address}\nNonce: ${nonce}`;
      const signature = await signMessageAsync({ message });
      // Exchange for Firebase custom token
      await signInWithWallet(address, signature, message, referralCode || undefined);
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.message || 'Wallet sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      if (isSignUp) {
        const checks = computeChecks(password);
        const allOk = checks.length && checks.upper && checks.lower && checks.number && checks.special;
        if (!allOk) {
          setError('Password must have 6+ chars, uppercase, lowercase, number, and special character.');
          return;
        }
        if (password !== confirmPassword) {
          setError('Passwords do not match.');
          return;
        }
        await signUpWithEmail(email, password, referralCode || undefined);
        setShowVerificationMessage(true);
        setVerificationSent(true);
      } else {
        const user = await signInWithEmail(email, password);
        if (!user.emailVerified) {
          setShowVerificationMessage(true);
          setError('Please verify your email before signing in.');
          return;
        }
        onOpenChange(false);
      }
    } catch (e: any) {
      const code = e?.code as string | undefined;
      let msg = 'Authentication failed';
      if (code === 'auth/email-already-in-use') msg = 'This email is already registered.';
      else if (code === 'auth/weak-password') msg = 'Password does not meet policy requirements.';
      else if (code === 'auth/password-does-not-meet-requirements') msg = 'Password does not meet policy requirements.';
      else if (code === 'auth/invalid-email') msg = 'Invalid email address.';
      else if (code === 'auth/user-not-found') msg = 'No account found with this email.';
      else if (code === 'auth/wrong-password') msg = 'Incorrect password.';
      else if (e?.message) msg = e.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await resendVerificationEmail();
      setVerificationSent(true);
    } catch {
      setError('Failed to resend verification email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-background/95 border-2 shadow-lg backdrop-blur-sm border-[var(--color-header-accent,#0A74FF)]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <img src="/Dynamic Header Logos/MdbApp.io Default Logo.png" alt="MdbApp" className="w-10 h-10 rounded" />
            {showReset ? 'Reset Password' : (isSignUp ? 'Create Account' : 'Sign In')}
          </DialogTitle>
          <DialogDescription className="sr-only">Sign in with Google or email</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">{error}</div>
        )}

        {!showReset && showVerificationMessage && (
          <div className="text-sm text-yellow-500 bg-yellow-500/10 border border-yellow-500/30 rounded px-3 py-2">
            {isSignUp ? (
              <div>
                <strong>Account created!</strong> Check your inbox to verify your email.
                {verificationSent && <div className="text-[var(--color-header-accent,#0A74FF)] mt-1">Verification email sent</div>}
                {!isConnected && (
                  <div className="text-xs text-muted-foreground text-center bg-accent/10 border rounded px-3 py-2" style={{ borderColor: 'var(--color-header-accent,#0A74FF)' }}>
                    To use wallet authentication, connect your EVM wallet using the button in the header first.
                  </div>
                )}
              </div>
            ) : (
              <div>
                <strong>Email not verified.</strong> Please verify your email. {verificationSent && <span className="text-[#0A74FF]">Verification email sent</span>}
              </div>
            )}
            <button
              onClick={handleResendVerification}
              disabled={loading}
              className="mt-2 w-full text-sm font-semibold border-2 rounded px-3 py-2 hover:bg-accent"
              style={{ borderColor: 'var(--color-header-accent,#0A74FF)' }}
            >
              {loading ? 'Sending...' : 'Resend Verification Email'}
            </button>
          </div>
        )}

        {!showReset && (
          <div className="space-y-3">
            <button
              onClick={handleWallet}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded px-3 py-2 font-semibold border-2 text-black dark:text-white hover:bg-accent/20"
              style={{ borderColor: 'var(--color-header-accent,#0A74FF)' }}
              title={isConnected && address ? `Connected: ${address.slice(0, 6)}...${address.slice(-4)}` : 'Connect your wallet first'}
            >
              {/* Simple wallet glyph */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M21 7H5a2 2 0 0 0-2 2v7a3 3 0 0 0 3 3h15V7Zm0 10h-5a2 2 0 0 1 0-4h5v4Zm-5-3h4v2h-4a1 1 0 0 1 0-2ZM19 5h-2.382l.724-1.447A1 1 0 0 0 16.447 2H6a3 3 0 0 0-3 3 1 1 0 0 0 1 1h15a1 1 0 1 0 0-2Z"/></svg>
              {isConnected && address ? 'Continue with Wallet' : 'Continue with Wallet'}
              {isConnected && address && (
                <span className="ml-auto text-xs bg-green-500/10 border border-green-500/30 text-green-500 px-2 py-0.5 rounded">✓ Connected</span>
              )}
            </button>

            <button
              onClick={handleGoogle}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded border-2 px-3 py-2 font-semibold text-black dark:text-white hover:bg-accent/20"
              style={{ borderColor: 'var(--color-header-accent,#0A74FF)' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <button
              onClick={handleTwitter}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded px-3 py-2 font-semibold border-2 text-black dark:text-white hover:bg-accent/20"
              style={{ borderColor: 'var(--color-header-accent,#0A74FF)' }}
            >
              <XLogo className="w-4 h-4" />
              Continue with X
            </button>

            <div className="flex items-center text-sm text-muted-foreground"><span className="flex-1 h-px bg-border" />
              <span className="px-3">or</span>
              <span className="flex-1 h-px bg-border" />
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <div>
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded border bg-background px-3 py-2 focus:outline-none focus:ring-2"
                  style={{ boxShadow: 'none', borderColor: 'var(--color-header-accent, #0A74FF)' }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 2px var(--color-header-accent, #0A74FF)`)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
                />
              </div>
              <div>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => updatePassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full rounded border bg-background px-3 py-2 focus:outline-none focus:ring-2"
                  style={{ boxShadow: 'none', borderColor: 'var(--color-header-accent, #0A74FF)' }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 2px var(--color-header-accent, #0A74FF)`)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
                />
              </div>
              {isSignUp && (
                <div className="text-xs grid grid-cols-2 gap-2 text-muted-foreground">
                  <div className={passwordChecks.length ? 'text-green-600' : ''}>• 6+ characters</div>
                  <div className={passwordChecks.upper ? 'text-green-600' : ''}>• Uppercase</div>
                  <div className={passwordChecks.lower ? 'text-green-600' : ''}>• Lowercase</div>
                  <div className={passwordChecks.number ? 'text-green-600' : ''}>• Number</div>
                  <div className={passwordChecks.special ? 'text-green-600' : ''}>• Special character</div>
                </div>
              )}
              {isSignUp && (
                <div>
                  <input
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-2 w-full rounded border bg-background px-3 py-2 focus:outline-none focus:ring-2"
                    style={{ boxShadow: 'none', borderColor: 'var(--color-header-accent, #0A74FF)' }}
                    onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 2px var(--color-header-accent, #0A74FF)`)}
                    onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
                  />
                </div>
              )}
              <div>
                <input
                  type="text"
                  placeholder="Referral code (optional)"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  className="w-full rounded border bg-background px-3 py-2 focus:outline-none focus:ring-2"
                  style={{ boxShadow: 'none', borderColor: 'var(--color-header-accent, #0A74FF)' }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 2px var(--color-header-accent, #0A74FF)`)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
                />
                <p className="text-xs text-muted-foreground mt-1">Have a referral code? Enter it here to support your referrer!</p>
              </div>
              <button
                type="submit"
                disabled={loading || (isSignUp && !(passwordChecks.length && passwordChecks.upper && passwordChecks.lower && passwordChecks.number && passwordChecks.special && password === confirmPassword))}
                className="w-full inline-flex items-center justify-center gap-2 rounded border-2 px-3 py-2 font-semibold hover:bg-accent"
                style={{ borderColor: 'var(--color-header-accent,#0A74FF)' }}
              >
                {loading ? (isSignUp ? 'Creating Account...' : 'Signing In...') : (isSignUp ? 'Create Account' : 'Sign In')}
              </button>
            </form>

            <div className="text-center text-sm text-muted-foreground pt-2 border-t">
              {isSignUp ? (
                <span>
                  Already have an account?{' '}
                  <button type="button" onClick={() => { setIsSignUp(false); setError(''); }} className="font-semibold underline text-[var(--color-header-accent,#0A74FF)]">Sign in</button>
                </span>
              ) : (
                <span>
                  Don't have an account?{' '}
                  <button type="button" onClick={() => { setIsSignUp(true); setError(''); }} className="font-semibold underline text-[var(--color-header-accent,#0A74FF)]">Sign up</button>
                </span>
              )}
            </div>
            {!isSignUp && (
              <div className="text-right text-xs">
                <button type="button" className="underline text-[var(--color-header-accent,#0A74FF)]" onClick={() => { setShowReset(true); setError(''); setResetSent(false); }}>
                  Forgot password?
                </button>
              </div>
            )}
          </div>
        )}

        {showReset && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Enter your account email and we'll send a password reset link.</p>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded border bg-background px-3 py-2 focus:outline-none focus:ring-2"
              style={{ boxShadow: 'none', borderColor: 'var(--color-header-accent, #0A74FF)' }}
              onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 2px var(--color-header-accent, #0A74FF)`)}
              onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
            />
            <button
              onClick={async () => {
                try {
                  setLoading(true);
                  setError('');
                  setResetSent(false);
                  await resetPassword(email);
                  setResetSent(true);
                } catch (e: any) {
                  const code = e?.code as string | undefined;
                  let msg = 'Failed to send reset email';
                  if (code === 'auth/invalid-email') msg = 'Invalid email address.';
                  else if (code === 'auth/user-not-found') msg = 'No account found with this email.';
                  else if (e?.message) msg = e.message;
                  setError(msg);
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded border-2 px-3 py-2 font-semibold hover:bg-accent"
              style={{ borderColor: 'var(--color-header-accent,#0A74FF)' }}
            >
              {loading ? 'Sending...' : 'Send reset email'}
            </button>
            {resetSent && (
              <div className="text-sm text-green-600 bg-green-500/10 border border-green-500/30 rounded px-3 py-2">
                Password reset email sent. Check your inbox.
              </div>
            )}
            <div className="text-center text-sm">
              <button type="button" className="underline text-[var(--color-header-accent,#0A74FF)]" onClick={() => setShowReset(false)}>Back to sign in</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
